import type { ConditionContext } from './conditions';

/**
 * Markup condicional inline: `[if <cond>]contenido[/if]`.
 *
 * `<cond>` es una expresión simple de atributos `key=value`:
 *   item=<id>      → hay al menos 1 del item
 *   noitem=<id>    → NO hay ese item
 *   flag=<id>      → la flag es true
 *   noflag=<id>    → la flag es false (o no existe)
 *   skill=<id>     → la skill está desbloqueada
 *   noskill=<id>   → la skill NO está desbloqueada
 *   spell=<id>     → el hechizo está aprendido
 *   faccion=<id>   → la facción es conocida (flag `conocido_<id>`)
 *   stat=<key>     con `gte=N` → stat >= N
 *
 * El contenido interno puede llevar otro markup (`[emph]`, `[shake]`...).
 * No se soporta anidación de `[if]` dentro de otro `[if]`.
 *
 * Filosofía: lo resolvemos a NIVEL DE TEXTO antes del parser de markup
 * principal. Si la condición falla, el tramo desaparece entero.
 */

const IF_REGEX = /\[if\s+([^\]]+)\]([\s\S]*?)\[\/if\]/g;

function parseAttrs(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([a-z][a-z0-9_]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s\]]+))/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    const key = m[1].toLowerCase();
    const val = m[2] ?? m[3] ?? m[4] ?? '';
    out[key] = val;
  }
  return out;
}

function evaluarAttrs(attrs: Record<string, string>, ctx: ConditionContext): boolean {
  if (attrs.item) {
    return ctx.personaje.inventario.some((i) => i.id === attrs.item && i.cantidad > 0);
  }
  if (attrs.noitem) {
    return !ctx.personaje.inventario.some(
      (i) => i.id === attrs.noitem && i.cantidad > 0,
    );
  }
  if (attrs.flag) {
    return ctx.flags[attrs.flag] === true;
  }
  if (attrs.noflag) {
    return ctx.flags[attrs.noflag] !== true;
  }
  if (attrs.skill) {
    return ctx.personaje.skillsDesbloqueadas.includes(attrs.skill);
  }
  if (attrs.noskill) {
    return !ctx.personaje.skillsDesbloqueadas.includes(attrs.noskill);
  }
  if (attrs.spell) {
    return ctx.personaje.hechizosAprendidos.includes(attrs.spell);
  }
  if (attrs.faccion) {
    return ctx.flags[`conocido_${attrs.faccion}`] === true;
  }
  if (attrs.stat) {
    const key = attrs.stat as keyof ConditionContext['personaje']['stats'];
    const valor = ctx.personaje.stats[key] ?? 0;
    if (attrs.gte) return valor >= Number(attrs.gte);
    if (attrs.lte) return valor <= Number(attrs.lte);
    return valor > 0;
  }
  // Por default, si no entendemos los attrs, ocultamos el tramo (conservador).
  return false;
}

/**
 * Resuelve todos los `[if ...]...[/if]` en `texto` usando el contexto.
 * Devuelve el texto resultante. Si el texto no tiene `[if]`, devuelve el
 * mismo string (sin allocar).
 *
 * Además limpia artefactos comunes de puntuación que quedarían feos
 * cuando un tramo desaparece: espacios dobles, espacios pegados a comas
 * y puntuación huérfana tipo "—  —".
 */
export function resolverCondicionalesEnTexto(
  texto: string,
  ctx: ConditionContext,
): string {
  if (!texto.includes('[if')) return texto;
  const sinIfs = texto.replace(IF_REGEX, (_, attrsStr: string, content: string) => {
    const attrs = parseAttrs(attrsStr);
    return evaluarAttrs(attrs, ctx) ? content : '';
  });
  // Limpieza suave de artefactos
  return sinIfs
    .replace(/\s+([,.;:!?])/g, '$1') // espacio antes de puntuación
    .replace(/—\s*—/g, '—') // guiones dobles
    .replace(/\(\s*\)/g, '') // paréntesis vacíos
    .replace(/[ \t]{2,}/g, ' '); // espacios dobles
}
